import { getCookie, setCookie } from 'cookies-next';
import type { i18n as I18nInstance } from 'i18next';
import { useEffect } from 'react';

import { countryToLocale, LOCALE_COOKIE, LOCALE_COOKIE_MAX_AGE } from '@/lib/i18n/settings';

const GEO_ENDPOINT = 'https://get.geojs.io/v1/ip/country.json';

/**
 * Hybrid locale detection, step 2: when neither an explicit choice (cookie /
 * ?lang=) nor the browser languages matched a supported locale, look up the
 * visitor's country by IP and map it to a language. The result (even "en") is
 * cached in the locale cookie so the lookup runs at most once per visitor.
 */
const useGeoLocaleFallback = (i18n: I18nInstance, needsGeoFallback: boolean) => {
  useEffect(() => {
    if (!needsGeoFallback || getCookie(LOCALE_COOKIE)) {
      return undefined;
    }
    let isCancelled = false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 4000);

    fetch(GEO_ENDPOINT, { signal: controller.signal })
      .then((response) => response.json())
      .then(({ country }: { country?: string }) => {
        if (isCancelled) return;
        const locale = countryToLocale(country);
        setCookie(LOCALE_COOKIE, locale, { maxAge: LOCALE_COOKIE_MAX_AGE, path: '/', sameSite: 'lax' });
        if (locale !== i18n.language) {
          i18n.changeLanguage(locale);
        }
      })
      .catch(() => {
        // offline / blocked / timed out — silently keep the default language
      })
      .finally(() => clearTimeout(timer));

    return () => {
      isCancelled = true;
      controller.abort();
      clearTimeout(timer);
    };
  }, [needsGeoFallback, i18n]);
};

export default useGeoLocaleFallback;
